import React, { useState, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export interface BlogType {
  id: string;
  name: string;
  title: string;
  description: string;
  images: File[];
  videoLinks: string[];
  socialLinks: string[];
  sections: { heading: string; description: string }[];
}

interface BlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (blog: BlogType) => void;
}

const MIN_WORDS = 1;
const MAX_WORDS = 150;
const MAX_IMAGES = 2;
const MAX_IMAGE_SIZE_MB = 2;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

console.log("APIS BASE URL ", API_BASE_URL);

function wordCount(str: string) {
  return str.trim().split(/\s+/).filter(Boolean).length;
}

// Enhanced error extraction to handle nested error objects and field errors
function extractErrorMessage(errorObj: any): string {
  if (!errorObj) return "Failed to save blog.";
  if (typeof errorObj === "string") return errorObj;
  if (typeof errorObj.message === "string" && errorObj.message.trim())
    return errorObj.message;
  // If errors is an object with field errors, join them
  if (
    errorObj.errors &&
    typeof errorObj.errors === "object" &&
    !Array.isArray(errorObj.errors)
  ) {
    // e.g. {images: "No image files received or file format is incorrect."}
    return Object.values(errorObj.errors)
      .filter((v) => typeof v === "string" && v.trim())
      .join(", ");
  }
  if (Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
    return errorObj.errors.join(", ");
  }
  return JSON.stringify(errorObj);
}

const BlogModal: React.FC<BlogModalProps> = ({ isOpen, onClose, onSave }) => {
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [videoLinks, setVideoLinks] = useState<string[]>([""]);
  const [socialLinks, setSocialLinks] = useState<string[]>([""]);
  const [sections, setSections] = useState([{ heading: "", description: "" }]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSectionChange = (
    idx: number,
    field: "heading" | "description",
    value: string
  ) => {
    setSections((prev) =>
      prev.map((sec, i) => (i === idx ? { ...sec, [field]: value } : sec))
    );
  };

  const addSection = () => {
    setSections((prev) => [...prev, { heading: "", description: "" }]);
  };

  const removeSection = (idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSocialLinkChange = (idx: number, value: string) => {
    setSocialLinks((prev) => prev.map((link, i) => (i === idx ? value : link)));
  };

  const addSocialLink = () => {
    setSocialLinks((prev) => [...prev, ""]);
  };

  const removeSocialLink = (idx: number) => {
    setSocialLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleVideoLinkChange = (idx: number, value: string) => {
    setVideoLinks((prev) => prev.map((link, i) => (i === idx ? value : link)));
  };

  const addVideoLink = () => {
    setVideoLinks((prev) => [...prev, ""]);
  };

  const removeVideoLink = (idx: number) => {
    setVideoLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    let newImages = [...images];
    let errorMsg = "";
    for (let file of files) {
      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        errorMsg = `Each image must be less than ${MAX_IMAGE_SIZE_MB}MB.`;
        break;
      }
      if (newImages.length < MAX_IMAGES) {
        newImages.push(file);
      }
    }
    if (newImages.length > MAX_IMAGES) {
      errorMsg = `You can upload up to ${MAX_IMAGES} images.`;
      newImages = newImages.slice(0, MAX_IMAGES);
    }
    setImages(newImages);
    setErrors((prev) => ({ ...prev, images: errorMsg }));
  };

  const removeImage = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const validate = () => {
    let errs: { [key: string]: string } = {};
    const nameWords = wordCount(name);
    const titleWords = wordCount(title);
    if (!name && !title) {
      errs.name = "Either Name or Title is required.";
      errs.title = "Either Name or Title is required.";
    } else {
      if (name && (nameWords < MIN_WORDS || nameWords > MAX_WORDS)) {
        errs.name = `Name must be between ${MIN_WORDS} and ${MAX_WORDS} words.`;
      }
      if (title && (titleWords < MIN_WORDS || titleWords > MAX_WORDS)) {
        errs.title = `Title must be between ${MIN_WORDS} and ${MAX_WORDS} words.`;
      }
    }
    const descWords = wordCount(description);
    if (!description) {
      errs.description = "Description is required.";
    } else if (descWords < MIN_WORDS || descWords > MAX_WORDS) {
      errs.description = `Description must be between ${MIN_WORDS} and ${MAX_WORDS} words.`;
    }
    if (images.length > MAX_IMAGES) {
      errs.images = `You can upload up to ${MAX_IMAGES} images.`;
    }
    for (let img of images) {
      if (img.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        errs.images = `Each image must be less than ${MAX_IMAGE_SIZE_MB}MB.`;
        break;
      }
    }
    if (!socialLinks.some((link) => link.trim())) {
      errs.socialLinks = "At least one social media link is required.";
    }
    for (let link of videoLinks) {
      if (link.trim() && !/^https?:\/\/.+/.test(link.trim())) {
        errs.videoLinks = "Video links must be valid URLs.";
        break;
      }
    }
    if (
      !sections.length ||
      !sections.some((sec) => sec.heading.trim() || sec.description.trim())
    ) {
      errs.sections = "At least one section is required.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // REWRITE: Now send all form data (including images) to the server using FormData.
  const handleSave = async () => {
    if (!validate()) {
      return;
    }
    setLoading(true);
    try {
      const blogId = "blog-" + Date.now();
      // Prepare filtered sections
      const filteredSections = sections.filter(
        (sec) => sec.heading.trim() || sec.description.trim()
      );

      // Prepare FormData for all fields including images
      const formData = new FormData();
      formData.append("id", blogId);
      formData.append("name", name);
      formData.append("title", title);
      formData.append("description", description);

      // Append images
      images.forEach((img, idx) => {
        formData.append("images[]", img, img.name);
      });

      // Append videoLinks and socialLinks as JSON string
      formData.append(
        "videoLinks",
        JSON.stringify(videoLinks.filter((link) => link.trim()))
      );
      formData.append(
        "socialLinks",
        JSON.stringify(socialLinks.filter((link) => link.trim()))
      );
      formData.append("sections", JSON.stringify(filteredSections));

      // Send FormData to the server
      // const response = await fetch("http://localhost/website/save_blogs.php", {
      const response = await fetch(`${API_BASE_URL}/save_blogs.php`, {
        method: "POST",
        body: formData,
      });

      let errorText = "";
      let errorFields: { [key: string]: string } = {};
      if (!response.ok) {
        try {
          const errorJson = await response.json();
          if (
            errorJson &&
            typeof errorJson === "object" &&
            errorJson.errors &&
            typeof errorJson.errors === "object" &&
            !Array.isArray(errorJson.errors)
          ) {
            errorFields = { ...errorJson.errors };
            errorText = extractErrorMessage(errorJson);
          } else {
            errorText = extractErrorMessage(errorJson);
          }
        } catch (e) {
          errorText = await response.text();
        }
        if (Object.keys(errorFields).length > 0) {
          setErrors((prev) => ({
            ...prev,
            ...errorFields,
            api: errorText || "Failed to save blog.",
          }));
        } else {
          setErrors({ api: errorText || "Failed to save blog." });
        }
        toast.error(errorText || "Failed to save blog.", {
          position: "top-right",
        });
        setLoading(false);
        return;
      }
      let savedBlog: any = null;
      try {
        savedBlog = await response.json();
        if (savedBlog && savedBlog.success === false) {
          let msg = extractErrorMessage(savedBlog);
          if (
            savedBlog.errors &&
            typeof savedBlog.errors === "object" &&
            !Array.isArray(savedBlog.errors)
          ) {
            setErrors((prev) => ({
              ...prev,
              ...savedBlog.errors,
              api: msg,
            }));
          } else {
            setErrors({ api: msg });
          }
          toast.error(msg, {
            position: "top-right",
          });
          setLoading(false);
          return;
        }
      } catch (e) {
        savedBlog = null;
      }

      toast.success("Blog saved successfully!", {
        position: "top-right",
        autoClose: 4000,
      });
      alert("Blog Saved");

      // Pass images to the local onSave as well
      onSave(
        savedBlog || {
          id: blogId,
          name,
          title,
          description,
          images,
          videoLinks: videoLinks.filter((link) => link.trim()),
          socialLinks: socialLinks.filter((link) => link.trim()),
          sections: filteredSections,
        }
      );

      onClose();
      setName("");
      setTitle("");
      setDescription("");
      setImages([]);
      setVideoLinks([""]);
      setSocialLinks([""]);
      setSections([{ heading: "", description: "" }]);
      setErrors({});
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err: any) {
      let errorMsg = err?.message || "Failed to save blog.";
      if (err && typeof err === "object" && err.response && err.response.data) {
        const data = err.response.data;
        let errorFields: { [key: string]: string } = {};
        if (
          data &&
          typeof data === "object" &&
          data.errors &&
          typeof data.errors === "object" &&
          !Array.isArray(data.errors)
        ) {
          errorFields = { ...data.errors };
          errorMsg = extractErrorMessage(data);
        } else {
          errorMsg = extractErrorMessage(data);
        }
        if (Object.keys(errorFields).length > 0) {
          setErrors((prev) => ({
            ...prev,
            ...errorFields,
            api: errorMsg,
          }));
        } else {
          setErrors({ api: errorMsg });
        }
      } else {
        setErrors({ api: errorMsg });
      }
      toast.error(errorMsg, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <ToastContainer />
      <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
        <div
          className="bg-white rounded-xl shadow-2xl p-0 w-[90vw] max-w-[90vw] max-h-[90vh] relative flex flex-col"
          style={{ height: "90vh" }}
        >
          <button
            className="absolute top-3 right-4 text-gray-500 text-3xl hover:text-gray-700 z-10"
            onClick={onClose}
          >
            &times;
          </button>
          <h3 className="text-2xl font-bold mb-0 px-8 pt-8 pb-4 border-b border-gray-200">
            <span>+ </span> Add New Blog
          </h3>
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <form
              className="space-y-8"
              onSubmit={async (e) => {
                e.preventDefault();
                await handleSave();
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Blog Name
                  </label>
                  <input
                    className={`w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                      errors.name ? "border-red-500" : "border-gray-300"
                    }`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={`Enter blog name (${MIN_WORDS}-${MAX_WORDS} words)`}
                  />
                  {errors.name && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.name}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block mb-1 font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    className={`w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                      errors.title ? "border-red-500" : "border-gray-300"
                    }`}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={`Enter title (${MIN_WORDS}-${MAX_WORDS} words)`}
                  />
                  {errors.title && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.title}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    className={`w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-400 transition ${
                      errors.description ? "border-red-500" : "border-gray-300"
                    }`}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={`Enter description (${MIN_WORDS}-${MAX_WORDS} words)`}
                    rows={4}
                  />
                  {errors.description && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.description}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium text-gray-700">
                    Upload Images (max {MAX_IMAGES}, each &lt;{" "}
                    {MAX_IMAGE_SIZE_MB}MB)
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className={`block w-full border rounded-lg p-2 ${
                      errors.images ? "border-red-500" : "border-gray-300"
                    }`}
                    disabled={images.length >= MAX_IMAGES}
                  />
                  <div className="flex flex-wrap mt-2 gap-3">
                    {images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={URL.createObjectURL(img)}
                          alt={`preview-${idx}`}
                          className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow hover:bg-red-600 transition"
                          onClick={() => removeImage(idx)}
                          title="Remove"
                        >
                          <span className="text-lg font-bold">−</span>
                        </button>
                      </div>
                    ))}
                  </div>
                  {errors.images && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.images}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium text-gray-700">
                    Video Links
                  </label>
                  <div className="space-y-2">
                    {videoLinks.map((link, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          className="flex-1 border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          value={link}
                          onChange={(e) =>
                            handleVideoLinkChange(idx, e.target.value)
                          }
                          placeholder="https://your-video-link.com"
                        />
                        {videoLinks.length > 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                            onClick={() => removeVideoLink(idx)}
                            title="Remove"
                          >
                            <span className="text-lg font-bold">−</span>
                          </button>
                        )}
                        {idx === videoLinks.length - 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                            onClick={addVideoLink}
                            title="Add"
                          >
                            <span className="text-lg font-bold">+</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.videoLinks && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.videoLinks}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-1 font-medium text-gray-700">
                    Social Media Links
                  </label>
                  <div className="space-y-2">
                    {socialLinks.map((link, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          className={`flex-1 border rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                            errors.socialLinks
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          value={link}
                          onChange={(e) =>
                            handleSocialLinkChange(idx, e.target.value)
                          }
                          placeholder="https://your-social-link.com"
                        />
                        {socialLinks.length > 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                            onClick={() => removeSocialLink(idx)}
                            title="Remove"
                          >
                            <span className="text-lg font-bold">−</span>
                          </button>
                        )}
                        {idx === socialLinks.length - 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                            onClick={addSocialLink}
                            title="Add"
                          >
                            <span className="text-lg font-bold">+</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {errors.socialLinks && (
                    <div className="text-red-500 text-xs mt-1">
                      {errors.socialLinks}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  Sections
                </label>
                <div className="space-y-3">
                  {sections.map((section, idx) => (
                    <div
                      key={idx}
                      className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center"
                    >
                      <input
                        className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Heading"
                        value={section.heading}
                        onChange={(e) =>
                          handleSectionChange(idx, "heading", e.target.value)
                        }
                      />
                      <input
                        className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Description"
                        value={section.description}
                        onChange={(e) =>
                          handleSectionChange(
                            idx,
                            "description",
                            e.target.value
                          )
                        }
                      />
                      <div className="flex gap-2 mt-2 md:col-span-2">
                        {sections.length > 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                            onClick={() => removeSection(idx)}
                            title="Remove Section"
                          >
                            <span className="text-lg font-bold">−</span>
                          </button>
                        )}
                        {idx === sections.length - 1 && (
                          <button
                            type="button"
                            className="w-8 h-8 flex items-center justify-center bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                            onClick={addSection}
                            title="Add Section"
                          >
                            <span className="text-lg font-bold">+</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {errors.sections && (
                  <div className="text-red-500 text-xs mt-1">
                    {errors.sections}
                  </div>
                )}
              </div>
              {errors.api && (
                <div className="text-red-500 text-xs mt-2">{errors.api}</div>
              )}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Blog"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogModal;
